import React from 'react';
import styled from 'styled-components';
import {
  FiEdit2,
  FiMapPin,
  FiMap,
  FiNavigation,
  FiPhone,
  FiInfo,
  FiClock,
} from 'react-icons/fi';
import theme from '../../styles/theme';

const CardContainer = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: ${theme.colors.primary[300]};
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%);
  padding: ${theme.spacing[6]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  color: ${theme.colors.text.primary};
  position: relative;
`;

const Title = styled.h3`
  margin: 0;
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const Description = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  font-size: ${theme.fontSizes.sm};
  line-height: 1.5;
`;

const StatusBadge = styled.span`
  align-self: flex-start;
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  background: ${props => (props.$active ? '#059669' : '#dc2626')};
  border: 1px solid ${props => (props.$active ? '#047857' : '#b91c1c')};
  color: #ffffff;
  font-size: ${theme.fontSizes.xs};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Content = styled.div`
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const SectionTitle = styled.h4`
  margin: 0;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
`;

const DetailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const DetailItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const DetailIcon = styled.span`
  color: ${theme.colors.primary[500]};
  font-size: ${theme.fontSizes.base};
  margin-top: 2px;
`;

const DetailValue = styled.span`
  line-height: 1.4;
  word-break: break-word;
`;

const ScheduleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
`;

const ScheduleItem = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
  align-items: center;
`;

const ScheduleDay = styled.span`
  font-weight: 600;
  color: ${theme.colors.text.primary};
`;

const LinksRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
`;

const LinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.primary[600]};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  background: transparent;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
    background: ${theme.colors.primary[50]};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
`;

const ActionButton = styled.button`
  flex: 1;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${props => (props.$danger ? theme.colors.error[500] : theme.colors.primary[500])};
  background: ${props => (props.$danger ? 'transparent' : theme.colors.primary[500])};
  color: ${props => (props.$danger ? theme.colors.error[500] : theme.colors.text.primary)};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => (props.$danger ? theme.colors.error[500] : theme.colors.primary[600])};
    color: ${theme.colors.text.primary};
  }
`;

const ActionIcon = styled.span`
  font-size: ${theme.fontSizes.base};
`;

const formatEndereco = (registro) => {
  const partes = [
    registro?.logradouro,
    registro?.numero,
    registro?.bairro,
  ].filter(Boolean);

  const cidadeEstado = [registro?.cidade, registro?.estado].filter(Boolean).join(' - ');
  if (cidadeEstado) {
    partes.push(cidadeEstado);
  }

  if (registro?.complemento) {
    partes.push(`Complemento: ${registro.complemento}`);
  }

  return partes.length > 0 ? partes.join(', ') : 'Endereço não informado';
};

const DIAS_LABELS = {
  1: 'Domingo',
  2: 'Segunda-feira',
  3: 'Terça-feira',
  4: 'Quarta-feira',
  5: 'Quinta-feira',
  6: 'Sexta-feira',
  7: 'Sábado',
};

const formatIntervalos = (horario) => {
  if (!horario.aberto) {
    return 'Fechado';
  }

  const intervalos = [];
  if (horario.hora_inicio_1 && horario.hora_fim_1) {
    intervalos.push(`${horario.hora_inicio_1} - ${horario.hora_fim_1}`);
  }
  if (horario.hora_inicio_2 && horario.hora_fim_2) {
    intervalos.push(`${horario.hora_inicio_2} - ${horario.hora_fim_2}`);
  }

  if (intervalos.length === 0) {
    return 'Aberto';
  }

  return intervalos.join(' | ');
};

const ClienteSobreCard = ({ registro, onEdit }) => {
  const clienteNome =
    registro?.clientes?.nome_fantasia ||
    registro?.clientes?.nome_social ||
    'Cliente não informado';

  const telefones = Array.isArray(registro?.telefones) ? registro.telefones : [];
  // Filtrar apenas o telefone principal (prioridade 0)
  const telefonePrincipal = telefones.find(t => t.prioridade === 0);
  const horarios = Array.isArray(registro?.horarios) ? registro.horarios : [];

  return (
    <CardContainer>
      <Header>
        <Title>
          <FiInfo />
          {clienteNome}
        </Title>
        {registro?.clientes && (
          <StatusBadge $active={registro?.clientes?.ativo}>
            {registro?.clientes?.ativo ? 'Cliente ativo' : 'Cliente inativo'}
          </StatusBadge>
        )}
      </Header>

      <Content>
        <Section>
          <SectionTitle>Localização</SectionTitle>
          <DetailList>
            <DetailItem>
              <DetailIcon><FiMapPin /></DetailIcon>
              <DetailValue>{formatEndereco(registro)}</DetailValue>
            </DetailItem>
          </DetailList>
        </Section>

        {(registro?.google_maps_url || registro?.waze_url) && (
          <Section>
            <SectionTitle>Localização por aplicativos</SectionTitle>
            <LinksRow>
              {registro?.google_maps_url && (
                <LinkButton href={registro.google_maps_url} target="_blank" rel="noopener noreferrer">
                  <FiMap />
                  Google Maps
                </LinkButton>
              )}
              {registro?.waze_url && (
                <LinkButton href={registro.waze_url} target="_blank" rel="noopener noreferrer">
                  <FiNavigation />
                  Waze
                </LinkButton>
              )}
            </LinksRow>
          </Section>
        )}

        <Section>
          <SectionTitle>Telefone Principal</SectionTitle>
          <DetailList>
            {!telefonePrincipal && (
              <DetailItem>
                <DetailIcon><FiPhone /></DetailIcon>
                <DetailValue>Nenhum telefone cadastrado.</DetailValue>
              </DetailItem>
            )}
            {telefonePrincipal && (
              <DetailItem key={telefonePrincipal?.id || `${telefonePrincipal.ddd}-${telefonePrincipal.numero}`}>
                <DetailIcon><FiPhone /></DetailIcon>
                <DetailValue>
                  ({telefonePrincipal.ddd}) {telefonePrincipal.numero}
                  {telefonePrincipal.contato ? ` • ${telefonePrincipal.contato}` : ''}
                </DetailValue>
              </DetailItem>
            )}
          </DetailList>
        </Section>

        <Section>
          <SectionTitle>
            <FiClock /> Horários de Funcionamento
          </SectionTitle>
          <ScheduleList>
            {horarios.length === 0 && (
              <ScheduleItem>
                <ScheduleDay>Informações não cadastradas.</ScheduleDay>
              </ScheduleItem>
            )}
            {horarios.map((horario) => (
              <ScheduleItem key={horario.id || horario.dia_semana}>
                <ScheduleDay>{DIAS_LABELS[horario.dia_semana] || `Dia ${horario.dia_semana}`}</ScheduleDay>
                <span>{formatIntervalos(horario)}</span>
              </ScheduleItem>
            ))}
          </ScheduleList>
        </Section>
      </Content>

      {onEdit && (
        <Actions>
          <ActionButton onClick={() => onEdit(registro)}>
            <ActionIcon><FiEdit2 /></ActionIcon>
            Editar
          </ActionButton>
        </Actions>
      )}
    </CardContainer>
  );
};

export default ClienteSobreCard;


